import React, { useState, useEffect, useMemo } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { patternAnalyticsService } from '../../lib/pattern-analytics-service';
import { patternSyncService } from '../../lib/pattern-sync-service';
import { 
  TrendingUp, 
  Clock, 
  Brain, 
  Target, 
  Activity,
  MessageCircle,
  Users,
  Sun,
  Calendar,
  BarChart3,
  PieChart,
  LineChart,
  ChevronDown,
  ChevronUp,
  Info,
  Star,
  AlertCircle
} from 'lucide-react';
import './AdvancedAnalyticsDashboard.css';

/**
 * Advanced Analytics Dashboard - Phase 4.1
 * Comprehensive pattern analysis and insights visualization
 */
export default function AdvancedAnalyticsDashboard() {
  const { currentUser } = useFamilyContext();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState(30);
  const [expandedSections, setExpandedSections] = useState({
    productivity: true,
    communication: false,
    coordination: false,
    environmental: false,
    goals: false,
    correlations: false,
    trends: false
  });
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      if (!currentUser) return;
      
      setIsLoading(true);
      try {
        const analysis = await patternAnalyticsService.analyzeUserPatterns(
          currentUser.id, 
          selectedTimeRange
        );
        setAnalytics(analysis);
        
        // Get sync status
        const status = patternSyncService.getSyncStatus(currentUser.id);
        setSyncStatus(status);
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalytics();
  }, [currentUser, selectedTimeRange]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (isLoading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Analyzing your patterns...</p>
        </div>
      </div>
    );
  }

  if (!analytics || analytics.confidence < 0.1) {
    return (
      <div className="analytics-empty">
        <Brain className="empty-icon" />
        <h3>Building Your Analytics</h3>
        <p>We need more data to provide meaningful insights. Keep using PaperSedge and check back soon!</p>
        <div className="data-quality">
          <p>Data Points: {analytics?.dataQuality?.totalDataPoints || 0}</p>
          <p>Minimum needed: 50+ interactions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="advanced-analytics-dashboard">
      <div className="analytics-header">
        <div className="header-content">
          <div className="header-title">
            <Brain className="title-icon" />
            <h1>Your Intelligence Analytics</h1>
          </div>
          <div className="header-meta">
            <div className="confidence-score">
              <Star className="confidence-icon" />
              <span>Confidence: {Math.round(analytics.confidence * 100)}%</span>
            </div>
            <select 
              value={selectedTimeRange} 
              onChange={(e) => setSelectedTimeRange(parseInt(e.target.value))}
              className="time-range-selector"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 2 weeks</option>
              <option value={30}>Last month</option>
              <option value={90}>Last 3 months</option>
            </select>
          </div>
        </div>
        
        <div className="analytics-summary">
          <div className="summary-stat">
            <div className="stat-value">{analytics.dataQuality.totalDataPoints}</div>
            <div className="stat-label">Data Points</div>
          </div>
          <div className="summary-stat">
            <div className="stat-value">{Math.round(analytics.dataQuality.timeSpan)}d</div>
            <div className="stat-label">Time Span</div>
          </div>
          <div className="summary-stat">
            <div className="stat-value">{analytics.recommendations.length}</div>
            <div className="stat-label">Insights</div>
          </div>
          {syncStatus && (
            <div className="summary-stat">
              <div className="stat-value sync-status" style={{
                color: syncStatus.isInitialized ? 'var(--color-emerald-600)' : 'var(--color-amber-600)'
              }}>
                {syncStatus.isInitialized ? '✓' : '⚠'}
              </div>
              <div className="stat-label">Cross-Device Sync</div>
              {syncStatus.lastSyncTime && (
                <div className="sync-time">
                  Last: {syncStatus.lastSyncTime.toLocaleTimeString()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="analytics-content">
        {/* Productivity Analysis */}
        <AnalyticsSection
          title="Productivity Patterns"
          icon={TrendingUp}
          isExpanded={expandedSections.productivity}
          onToggle={() => toggleSection('productivity')}
          confidence={analytics.productivity.energyCorrelation?.confidence || 0}
        >
          <ProductivityAnalysis data={analytics.productivity} />
        </AnalyticsSection>

        {/* Communication Analysis */}
        <AnalyticsSection
          title="Communication Patterns"
          icon={MessageCircle}
          isExpanded={expandedSections.communication}
          onToggle={() => toggleSection('communication')}
          confidence={0.7}
        >
          <CommunicationAnalysis data={analytics.communication} />
        </AnalyticsSection>

        {/* Family Coordination */}
        <AnalyticsSection
          title="Family Coordination"
          icon={Users}
          isExpanded={expandedSections.coordination}
          onToggle={() => toggleSection('coordination')}
          confidence={0.6}
        >
          <CoordinationAnalysis data={analytics.coordination} />
        </AnalyticsSection>

        {/* Environmental Patterns */}
        <AnalyticsSection
          title="Environmental Preferences"
          icon={Sun}
          isExpanded={expandedSections.environmental}
          onToggle={() => toggleSection('environmental')}
          confidence={0.5}
        >
          <EnvironmentalAnalysis data={analytics.environmental} />
        </AnalyticsSection>

        {/* Goal Achievement */}
        <AnalyticsSection
          title="Goal Achievement"
          icon={Target}
          isExpanded={expandedSections.goals}
          onToggle={() => toggleSection('goals')}
          confidence={0.8}
        >
          <GoalAnalysis data={analytics.goal_achievement} />
        </AnalyticsSection>

        {/* Pattern Correlations */}
        <AnalyticsSection
          title="Pattern Correlations"
          icon={BarChart3}
          isExpanded={expandedSections.correlations}
          onToggle={() => toggleSection('correlations')}
          confidence={0.6}
        >
          <CorrelationAnalysis data={analytics.correlations} />
        </AnalyticsSection>

        {/* Trends Analysis */}
        <AnalyticsSection
          title="Trends & Insights"
          icon={LineChart}
          isExpanded={expandedSections.trends}
          onToggle={() => toggleSection('trends')}
          confidence={0.7}
        >
          <TrendsAnalysis data={analytics.trends} recommendations={analytics.recommendations} />
        </AnalyticsSection>
      </div>
    </div>
  );
}

// Analytics Section Component
function AnalyticsSection({ title, icon: Icon, children, isExpanded, onToggle, confidence }) {
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'var(--color-emerald-500)';
    if (confidence >= 0.6) return 'var(--color-azure-500)';
    if (confidence >= 0.4) return 'var(--color-amber-500)';
    return 'var(--color-stone-400)';
  };

  return (
    <div className={`analytics-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="section-header" onClick={onToggle}>
        <div className="section-title">
          <Icon className="section-icon" />
          <span>{title}</span>
          {confidence > 0 && (
            <div className="confidence-indicator" style={{ color: getConfidenceColor(confidence) }}>
              {Math.round(confidence * 100)}%
            </div>
          )}
        </div>
        {isExpanded ? <ChevronUp /> : <ChevronDown />}
      </div>
      {isExpanded && (
        <div className="section-content">
          {children}
        </div>
      )}
    </div>
  );
}

// Productivity Analysis Component
function ProductivityAnalysis({ data }) {
  const peakHours = data.peakHours;
  const energyCorrelation = data.energyCorrelation;

  return (
    <div className="productivity-analysis">
      {peakHours.primary && (
        <div className="peak-hours">
          <h4><Clock className="inline-icon" /> Peak Productivity Hours</h4>
          <div className="peak-times">
            <div className="peak-time primary">
              <div className="time-label">Primary Peak</div>
              <div className="time-value">{formatHour(peakHours.primary.hour)}:00</div>
              <div className="time-score">Score: {peakHours.primary.score?.toFixed(2)}</div>
            </div>
            {peakHours.secondary && (
              <div className="peak-time secondary">
                <div className="time-label">Secondary Peak</div>
                <div className="time-value">{formatHour(peakHours.secondary.hour)}:00</div>
                <div className="time-score">Score: {peakHours.secondary.score?.toFixed(2)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {energyCorrelation.correlation !== 0 && (
        <div className="energy-correlation">
          <h4><Activity className="inline-icon" /> Energy-Productivity Correlation</h4>
          <div className="correlation-score" style={{
            color: energyCorrelation.correlation > 0.5 ? 'var(--color-emerald-600)' : 
                   energyCorrelation.correlation > 0.2 ? 'var(--color-amber-600)' : 
                   'var(--color-stone-600)'
          }}>
            {energyCorrelation.correlation > 0.5 ? 'Strong positive correlation' :
             energyCorrelation.correlation > 0.2 ? 'Moderate correlation' :
             'Weak correlation'}
          </div>
          <div className="correlation-value">{energyCorrelation.correlation.toFixed(3)}</div>
          {energyCorrelation.optimalEnergyLevel && (
            <div className="optimal-energy">
              Optimal energy level: <strong>{energyCorrelation.optimalEnergyLevel}</strong>
            </div>
          )}
        </div>
      )}

      {data.recommendations.length > 0 && (
        <div className="recommendations">
          <h4>Productivity Recommendations</h4>
          {data.recommendations.map((rec, index) => (
            <div key={index} className="recommendation">
              <Info className="rec-icon" />
              <span>{rec.text || rec.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Communication Analysis Component
function CommunicationAnalysis({ data }) {
  return (
    <div className="communication-analysis">
      {data.averageResponseTime > 0 && (
        <div className="response-time">
          <h4>Average Response Time</h4>
          <div className="time-value">{formatDuration(data.averageResponseTime)}</div>
        </div>
      )}

      {data.preferredWindows?.length > 0 && (
        <div className="preferred-windows">
          <h4>Preferred Communication Windows</h4>
          {data.preferredWindows.map((window, index) => (
            <div key={index} className="time-window">
              {window.start} - {window.end}
            </div>
          ))}
        </div>
      )}

      {data.recommendations.length > 0 && (
        <div className="recommendations">
          <h4>Communication Tips</h4>
          {data.recommendations.map((rec, index) => (
            <div key={index} className="recommendation">
              <MessageCircle className="rec-icon" />
              <span>{rec}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Other analysis components would be implemented similarly...
function CoordinationAnalysis({ data }) {
  return (
    <div className="coordination-analysis">
      <div className="success-rate">
        <h4>Coordination Success Rate</h4>
        <div className="rate-value">{Math.round(data.successRate * 100)}%</div>
      </div>
    </div>
  );
}

function EnvironmentalAnalysis({ data }) {
  return (
    <div className="environmental-analysis">
      <div className="weather-preferences">
        <h4>Weather Activity Preferences</h4>
        <p>Environmental pattern data is being collected...</p>
      </div>
    </div>
  );
}

function GoalAnalysis({ data }) {
  return (
    <div className="goal-analysis">
      <div className="achievement-rate">
        <h4>Goal Achievement Rate</h4>
        <div className="rate-value">{Math.round(data.achievementRate * 100)}%</div>
      </div>
    </div>
  );
}

function CorrelationAnalysis({ data }) {
  return (
    <div className="correlation-analysis">
      <h4>Pattern Correlations</h4>
      {Object.entries(data).map(([key, correlation]) => (
        <div key={key} className="correlation-item">
          <span className="correlation-label">{formatCorrelationLabel(key)}</span>
          <span className="correlation-value">{correlation?.toFixed(3) || 'N/A'}</span>
        </div>
      ))}
    </div>
  );
}

function TrendsAnalysis({ data, recommendations }) {
  return (
    <div className="trends-analysis">
      <h4>Key Insights</h4>
      {recommendations.map((rec, index) => (
        <div key={index} className="insight-item">
          <div className="insight-icon">
            {rec.type === 'productivity' ? <TrendingUp /> : <Info />}
          </div>
          <div className="insight-content">
            <div className="insight-title">{rec.title}</div>
            <div className="insight-description">{rec.description}</div>
            <div className="insight-confidence">Confidence: {Math.round(rec.confidence * 100)}%</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper functions
function formatHour(hour) {
  return hour === 0 ? '12 AM' : 
         hour < 12 ? `${hour} AM` : 
         hour === 12 ? '12 PM' : 
         `${hour - 12} PM`;
}

function formatDuration(minutes) {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}h ${mins}m`;
}

function formatCorrelationLabel(key) {
  const labels = {
    productivityEnergy: 'Productivity ↔ Energy',
    weatherActivity: 'Weather ↔ Activity',
    communicationCoordination: 'Communication ↔ Coordination'
  };
  return labels[key] || key;
}